/* eslint-disable react/prop-types */
import { DatePicker, Input, InputNumber, Select } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrayTextInput } from '../controls/ArrayTextInput';
import { DateRange } from '../controls/DateRange';
import { FDropdown } from '../controls/FDropdown';
import { FilterColumn, TRule } from '../filters.types';

type Inputs = { col: FilterColumn; value?: TRule['value'] } & {
  onChange: (value: TRule['value']) => void;
};

export const RuleValueControlPicker: React.FC<Inputs> = ({ col, value: initialValue, onChange }) => {
  const { t } = useTranslation();
  const { type } = col;
  const lastColNameRef = useRef<string | undefined>(col.name);

  // Convert initial value - use null for truly empty states instead of empty string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getInitialValue = () => {
    if (initialValue === undefined || initialValue === null) {
      // Return appropriate empty value based on type
      if (type === 'text') return '';
      return null;
    }
    return initialValue;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [value, setValue] = useState<any>(getInitialValue());

  // Check if column changed and update state immediately during render
  if (lastColNameRef.current !== undefined && lastColNameRef.current !== col.name) {
    lastColNameRef.current = col.name;
    const clearedValue = type === 'text' ? '' : null;
    // Update state immediately during render
    if (value !== clearedValue) {
      setValue(clearedValue);
    }
  } else if (lastColNameRef.current === undefined) {
    // Initialize ref on first render
    lastColNameRef.current = col.name;
  }

  // Sync local state when initialValue prop changes (e.g., when loading from URL)
  useEffect(() => {
    setValue(getInitialValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (event: any) => {
    setValue(event.target.value);
    onChange(event.target.value);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onValueChange = (value: any) => {
    setValue(value);
    onChange(value);
  };

  return (
    <div>
      {type === 'text' && <Input className="w-full" value={value || ''} onChange={handleChange} />}
      {type === 'number' && (
        <InputNumber
          className="w-full"
          style={{ width: '100%' }}
          min={1}
          max={10}
          value={value as number | null}
          onChange={onValueChange}
          placeholder={t('filters-enter-number-placeholder')}
        />
      )}
      {type === 'date' && (
        <DatePicker
          className="w-full"
          value={value ? dayjs(value) : undefined}
          onChange={(date) => onValueChange(date ? date.hour(12).minute(0).second(0).millisecond(0).toDate() : null)}
        />
      )}
      {type === 'date-range' && (
        <div className="w-full">
          <DateRange onChange={(date) => onValueChange(date)} />
        </div>
      )}
      {type === 'boolean' && (
        <Select
          className="w-full"
          options={
            col.options && col.options.length > 0
              ? col.options
              : [
                  { label: 'True', value: 1 },
                  { label: 'False', value: 0 },
                ]
          }
          value={value}
          onChange={onValueChange}
          placeholder={t('filters-select-value-placeholder')}
        />
      )}
      <FDropdown col={col} value={value} onChange={onValueChange} />
      {type === 'array-text' && (
        <div className="w-full">
          <ArrayTextInput value={value as string[]} onChange={onValueChange} placeholder={col.placeholder} />
        </div>
      )}
      {type === 'custom' && !!col.component && (
        <div className="w-full">
          <col.component onChange={onValueChange} value={value} />
        </div>
      )}
    </div>
  );
};
